import axios from 'axios'

// 创建axios实例
const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? 'https://bnn.chaite.cloud'
    : '/api', // 生产环境直连，开发环境使用代理
  timeout: 60000, // 60秒超时
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    console.log('发送请求:', config)
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => {
    console.log('收到响应:', response)
    return response
  },
  error => {
    console.error('响应错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 将任意图片格式转换为JPEG（模仿banana.js的bufferToJpeg功能）
 * 处理GIF（只取第一帧）、透明背景（转为白底）等
 * @param {File} file 原始图片文件
 * @param {number} maxSizeMB 最大大小（MB）
 * @param {number} quality 压缩质量 (0-1)
 * @returns {Promise<string>} JPEG格式的DataURL
 */
function convertToJpeg (file, maxSizeMB = 5, quality = 0.92) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      // 计算压缩后的尺寸
      let { width, height } = img
      const maxSize = 1920 // 最大边长

      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height * maxSize) / width
          width = maxSize
        } else {
          width = (width * maxSize) / height
          height = maxSize
        }
      }

      canvas.width = width
      canvas.height = height

      // 重要：先填充白色背景（处理透明图片）
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, width, height)

      // 绘制图片到白色背景上
      ctx.drawImage(img, 0, 0, width, height)

      // 尝试不同的质量直到满足大小要求
      let currentQuality = quality
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('图片转换失败'))
              return
            }

            const sizeMB = blob.size / (1024 * 1024)
            console.log(`图片转换: ${file.name}, 质量: ${currentQuality.toFixed(2)}, 大小: ${sizeMB.toFixed(2)}MB`)

            if (sizeMB <= maxSizeMB || currentQuality <= 0.1) {
              // 转换为DataURL
              const reader = new FileReader()
              reader.onload = e => {
                console.log(`图片转换完成: ${file.name}, 最终大小: ${sizeMB.toFixed(2)}MB`)
                resolve(e.target.result)
              }
              reader.onerror = reject
              reader.readAsDataURL(blob)
            } else {
              // 继续压缩
              currentQuality -= 0.1
              tryCompress()
            }
          },
          'image/jpeg', // 强制转换为JPEG格式
          currentQuality
        )
      }

      tryCompress()
    }

    img.onerror = () => reject(new Error(`图片格式不支持或文件损坏: ${file.name}`))

    // 关键：设置crossOrigin以避免CORS问题
    img.crossOrigin = 'anonymous'
    img.src = URL.createObjectURL(file)
  })
}

/**
 * 检测图片格式是否需要转换
 * @param {File} file 图片文件
 * @returns {boolean} 是否需要转换为JPEG
 */
function needsFormatConversion (file) {
  const type = file.type.toLowerCase()
  // GIF、PNG（可能有透明）、WEBP、BMP等都需要转换为JPEG
  return type !== 'image/jpeg' && type !== 'image/jpg'
}

/**
 * 将文件转换为JPEG格式的DataURL（模仿banana.js逻辑）
 * @param {File} file 图片文件
 * @returns {Promise<string>} JPEG格式的DataURL字符串
 */
export function fileToDataURL (file) {
  const fileSizeMB = file.size / (1024 * 1024)
  const needsConversion = needsFormatConversion(file)

  console.log(`处理图片: ${file.name}`)
  console.log(`- 格式: ${file.type}`)
  console.log(`- 大小: ${fileSizeMB.toFixed(2)}MB`)
  console.log(`- 需要转换: ${needsConversion ? '是' : '否'}`)

  // 如果是GIF、PNG等格式，或者文件过大，都需要转换处理
  if (needsConversion || fileSizeMB > 5) {
    console.log(`转换图片格式: ${file.name} -> JPEG`)
    return convertToJpeg(file, 5, 0.92)
  }

  // JPEG格式且文件不大，直接转换
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      console.log(`直接使用原图片: ${file.name}`)
      resolve(e.target.result)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * 构建API请求的messages格式
 * @param {string} text 操作描述文本
 * @param {Array} dataUrls 图片DataURL数组
 * @returns {Array} messages数组
 */
function buildMessages (text, dataUrls) {
  const content = []

  // 添加文本内容
  if (text && text.length) {
    content.push({ type: 'text', text })
  }

  // 添加图片内容
  for (const dataUrl of dataUrls) {
    content.push({
      type: 'image_url',
      image_url: { url: dataUrl }
    })
  }

  return [{ role: 'user', content }]
}

/**
 * 从响应文本中提取Markdown图片URL
 * @param {string} text API响应文本
 * @returns {Array<string>} 图片URL数组
 */
function extractMarkdownImageUrls (text) {
  const urls = []
  // 匹配 ![任意文本](URL) 格式
  const regex = /!\[[^\]]*\]\((\S+?)(?:\s+"[^"]*")?\)/g
  let match

  while ((match = regex.exec(text)) !== null) {
    let url = (match[1] || '').trim()
    // 移除可能的尖括号包装
    if (url.startsWith('<') && url.endsWith('>')) {
      url = url.slice(1, -1)
    }
    urls.push(url)
  }

  return urls
}

/**
 * 带重试机制的API调用
 * @param {Array} messages 请求消息
 * @param {number} maxRetries 最大重试次数
 * @returns {Promise<string>} API响应内容
 */
async function callAPIWithRetry (messages, maxRetries = 5) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`API调用尝试 ${attempt}/${maxRetries}`)

      const response = await api.post('/v1/chat/completions', {
        model: 'nano-banana',
        messages
      })

      if (!response.data) {
        throw new Error('API响应为空')
      }

      // 提取响应内容
      const content = String(
        response.data?.choices?.[0]?.message?.content ??
        response.data?.choices?.[0]?.delta?.content ??
        ''
      )

      if (content.trim().length > 0) {
        console.log(`API调用成功，尝试次数: ${attempt}`)
        return content
      }

      console.warn(`[API] 接口返回空内容，重试第 ${attempt} 次`)

      // 最后一次尝试时抛出错误
      if (attempt === maxRetries) {
        throw new Error('API返回内容为空')
      }
    } catch (error) {
      console.warn(`[API] 调用失败，重试第 ${attempt} 次:`, error?.message || error)

      // 最后一次尝试时抛出错误
      if (attempt === maxRetries) {
        throw error
      }

      // 等待一段时间后重试 (指数退避)
      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
      console.log(`等待 ${delayMs}ms 后重试...`)
      await new Promise(resolve => setTimeout(resolve, delayMs))
    }
  }

  throw new Error(`接口无返回内容（已重试 ${maxRetries} 次）`)
}

/**
 * 调用图片处理API
 * @param {string} operationText 操作描述
 * @param {Array<File>} imageFiles 图片文件数组
 * @param {number} maxRetries 最大重试次数
 * @returns {Promise<Object>} 处理结果
 */
export async function processImages (operationText, imageFiles, maxRetries = 5) {
  try {
    if (!operationText || !operationText.trim()) {
      throw new Error('请输入操作描述')
    }

    if (!imageFiles || imageFiles.length === 0) {
      throw new Error('请选择至少一张图片')
    }

    console.log('开始处理图片...', {
      imageCount: imageFiles.length,
      operation: operationText.slice(0, 50) + '...',
      maxRetries
    })

    // 转换图片为DataURL
    const dataUrls = []
    for (const file of imageFiles) {
      const dataUrl = await fileToDataURL(file)
      dataUrls.push(dataUrl)
    }

    // 构建请求消息
    const messages = buildMessages(operationText.trim(), dataUrls)

    // 发送API请求（带重试）
    const content = await callAPIWithRetry(messages, maxRetries)

    // 从响应中提取图片URL
    const imageUrls = extractMarkdownImageUrls(content)

    if (imageUrls.length === 0) {
      throw new Error(`未找到生成的图片。API返回内容：${content}`)
    }

    console.log('图片处理成功:', {
      generatedImages: imageUrls.length,
      responseLength: content.length
    })

    return {
      success: true,
      images: imageUrls,
      rawResponse: content
    }
  } catch (error) {
    console.error('API调用最终失败:', error)

    let errorMessage = '处理图片时发生错误'

    if (error.response) {
      // 服务器响应错误
      const status = error.response.status
      const statusText = error.response.statusText

      if (status >= 500) {
        errorMessage = `服务器内部错误 (${status})，请稍后重试`
      } else if (status === 429) {
        errorMessage = '请求频率过高，请稍后重试'
      } else if (status === 413) {
        errorMessage = '图片文件过大，请选择更小的图片或减少图片数量'
      } else if (status === 400) {
        errorMessage = '请求参数有误，请检查图片格式和描述'
      } else {
        errorMessage = `服务器错误: ${status} ${statusText}`
      }
    } else if (error.request) {
      // 网络错误
      errorMessage = '网络连接失败，请检查网络设置或稍后重试'
    } else if (error.message) {
      // 其他错误
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      rawError: error,
      retryCount: maxRetries
    }
  }
}

export default api
